/**
 * FormFill — fill, save-as-draft, complete, reopen and print a single
 * saved copy (form_submissions document) of a published JCC form.
 * Renders from the submission's own frozen `template_snapshot`, not
 * the live template — so a later admin edit never changes a copy
 * already in progress (requirement 6).
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, CheckCircle2, RotateCcw, Printer } from "lucide-react";
import { api, getFriendlyErrorMessage } from "../lib/api";
import HandoverGate from "../components/layout/HandoverGate";
import { FormFieldRenderer } from "../components/forms/FormFieldRenderer";
import { StandardBlockRenderer } from "../components/forms/StandardBlockRenderer";
import { RichTextDisplay } from "../components/forms/RichTextDisplay";

const ANY_MEMBER_ENTITLEMENT = ["carer_free", "carer_profile", "carer_essentials", "pro_carer", "family_free", "family_circle"];

function FormFillInner() {
  const { templateId, submissionId } = useParams();
  const [submission, setSubmission] = useState(null);
  const [answers, setAnswers] = useState({});
  const [standardAnswers, setStandardAnswers] = useState({});
  const [copyLabel, setCopyLabel] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/api/forms/submissions/${submissionId}`);
        if (!alive) return;
        setSubmission(data);
        setAnswers(data.answers || {});
        setStandardAnswers(data.standard_block_answers || {});
        setCopyLabel(data.copy_label || "");
      } catch (err) {
        if (alive) setError(getFriendlyErrorMessage(err));
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [submissionId]);

  // One quiet retry on a genuine connection failure (no response reached
  // the server at all) before surfacing an error — smooths over the
  // transient network blips that previously showed a generic, unhelpful
  // "Something went wrong" message with no indication of what happened.
  const putSubmission = async () => {
    const payload = { copy_label: copyLabel, standard_block_answers: standardAnswers, answers };
    try {
      return await api.put(`/api/forms/submissions/${submissionId}`, payload);
    } catch (err) {
      if (err?.response) throw err;
      await new Promise((r) => setTimeout(r, 1000));
      return await api.put(`/api/forms/submissions/${submissionId}`, payload);
    }
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const { data } = await putSubmission();
      setSubmission(data);
      toast.success("Saved.");
    } catch (err) {
      toast.error(getFriendlyErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const markComplete = async () => {
    setCompleting(true);
    try {
      await putSubmission();
      const { data } = await api.post(`/api/forms/submissions/${submissionId}/complete`);
      setSubmission(data);
      toast.success("Marked as complete.");
    } catch (err) {
      toast.error(getFriendlyErrorMessage(err));
    } finally {
      setCompleting(false);
    }
  };

  const reopen = async () => {
    try {
      const { data } = await api.post(`/api/forms/submissions/${submissionId}/reopen`);
      setSubmission(data);
      toast.success("Reopened for editing.");
    } catch (err) {
      toast.error(getFriendlyErrorMessage(err));
    }
  };

  if (!loaded) return <div className="container-jcc py-24 text-center text-ink-muted" data-testid="form-fill-loading">Loading…</div>;
  if (error || !submission) {
    return (
      <div className="container-jcc py-24 text-center" data-testid="form-fill-error">
        <p className="text-red-600 text-sm">{error || "Saved copy not found."}</p>
        <Link to={`/forms/${templateId}`} className="btn-outline text-sm mt-6 inline-flex">Back to form</Link>
      </div>
    );
  }

  const snapshot = submission.template_snapshot || {};
  const isCompleted = submission.status === "completed";

  return (
    <div className="container-jcc py-8" data-testid="form-fill-page">
      <div className="print:hidden max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <Link to={`/forms/${templateId}`} className="btn-outline text-sm" data-testid="form-fill-back-link">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <button type="button" onClick={() => window.print()} className="btn-outline text-sm" data-testid="form-fill-print-btn">
            <Printer className="w-4 h-4" /> Print this copy
          </button>
        </div>

        <label className="block mb-4">
          <span className="text-xs font-medium text-ink-muted">Copy name</span>
          <input value={copyLabel} onChange={(e) => setCopyLabel(e.target.value)}
                 className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="form-fill-copy-label-input" />
        </label>

        {isCompleted && (
          <div className="mb-4 flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-100 rounded-lg p-3" data-testid="form-fill-completed-banner">
            <CheckCircle2 className="w-4 h-4" /> This copy is marked complete.
            <button type="button" onClick={reopen} className="ml-auto text-teal font-medium inline-flex items-center gap-1" data-testid="form-fill-reopen-btn">
              <RotateCcw className="w-3.5 h-3.5" /> Reopen to edit
            </button>
          </div>
        )}

        <h1 className="font-display text-2xl font-bold">{snapshot.title}</h1>
        {snapshot.intro_text && (
          <div className="mt-2 bg-slate-50 border border-slate-100 rounded-lg p-3">
            <RichTextDisplay content={snapshot.intro_text} testId="form-fill-intro" />
          </div>
        )}

        <div className="mt-5">
          <StandardBlockRenderer standardBlock={snapshot.standard_block} answers={standardAnswers}
                                 onChange={(k, v) => setStandardAnswers((p) => ({ ...p, [k]: v }))} mode="fill" />
          <div className="space-y-4">
            {(snapshot.fields || []).map((field) => (
              <FormFieldRenderer key={field.field_id} field={field} value={answers[field.field_id]}
                                  onChange={(v) => setAnswers((p) => ({ ...p, [field.field_id]: v }))} mode="fill" />
            ))}
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3 sticky bottom-4">
          <button type="button" onClick={saveDraft} disabled={saving} className="btn-outline text-sm" data-testid="form-fill-save-draft-btn">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save draft"}
          </button>
          <button type="button" onClick={markComplete} disabled={completing} className="btn-teal text-sm" data-testid="form-fill-mark-complete-btn">
            <CheckCircle2 className="w-4 h-4" /> {completing ? "Saving…" : "Mark as complete"}
          </button>
        </div>
      </div>

      {/* Clean print-only render — omits nav, buttons and every screen control */}
      <div className="hidden print:block max-w-3xl mx-auto">
        <h1 className="font-display text-2xl font-bold">{snapshot.title}</h1>
        <p className="text-sm text-ink-muted">{copyLabel}</p>
        <div className="mt-5">
          <StandardBlockRenderer standardBlock={snapshot.standard_block} answers={standardAnswers} mode="print" />
          {(snapshot.fields || []).map((field) => (
            <FormFieldRenderer key={field.field_id} field={field} value={answers[field.field_id]} mode="print" />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function FormFill() {
  return (
    <HandoverGate requiredEntitlement={ANY_MEMBER_ENTITLEMENT}>
      <FormFillInner />
    </HandoverGate>
  );
}
